import { Component, ElementRef, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { ActivatedRoute, Router } from "@angular/router";

import { BaseComponent, buildCommunityPostPath, environment, FSP_COMMUNITY_STRING_ID, HelperService, htmlFragmentToText, UserService } from "@mypxplat/xplat/core";
import { FirebaseService, WebCommunityService, BetaService, SeoService } from "@mypxplat/xplat/web/core";
import { map, skip, takeUntil, throttle, throttleTime } from "rxjs";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community-post-detail",
  templateUrl: "community-post-detail.component.html",
  standalone: false,
})
export class CommunityPostDetailComponent extends BaseComponent {
  @Input() public betaStringId: string;
  /**
   * Community the detail page belongs to. Defaults to Fender Studio Pro so today's
   * `/community/post/:id` route (which doesn't carry a community segment) resolves to
   * FSP. When multi-community routing lands in DENG-727 task #37, the parent shell will
   * pass a resolved id from the route params.
   */
  @Input() public communityStringId: string = FSP_COMMUNITY_STRING_ID;

  /**
   * Rendered inside the community feed's split pane rather than as its own page.
   *
   * Drops the page chrome (the full-width container and the back-arrow header, both of
   * which belong to a routed page) and turns "back" into a `closed` event for the parent
   * to act on. Data loading is identical either way.
   */
  @Input() public embedded = false;

  /**
   * Rendered full-width INSIDE the community shell (narrow viewport, or the detailed feed)
   * rather than as a standalone page.
   *
   * The distinction matters only for "back". The shell still has the feed mounted behind
   * this post, so backing out is a toggle, not a navigation — it emits `closed` and lets
   * the shell put the list back instantly, on the right community, at the right scroll
   * position. `goBack()`'s cache-restore-and-navigate dance is for the genuinely standalone
   * page (beta's `/beta/betapost/:id`), which has nothing behind it.
   */
  @Input() public hosted = false;

  /** Emitted instead of navigating when the shell owns what is behind this post. */
  @Output() public closed: EventEmitter<void> = new EventEmitter();
  /** A channel chip on the post was clicked (its string id): the hub switches its feed to it. */
  @Output() public channelSelected: EventEmitter<string> = new EventEmitter();

  /**
   * Which post to show, when the parent knows (the split pane) rather than the route.
   *
   * A setter rather than a plain field so selecting a different row re-runs the load in
   * place — the pane stays mounted and swaps its contents, which is what makes clicking
   * down a list feel instant.
   */
  @Input() public set postIdInput(value: string) {
    if (!value || value === this.postId) return;
    this.postId = value;
    this._teardownWatchers();
    this._closeView();
    // The split pane keeps its scroll position across posts, so a new post would open wherever
    // the last one was left. Start at the top; `<myp-comment>` moves on to "New replies" or the
    // bottom once it knows you've read this thread before.
    const pane = this._host?.nativeElement?.closest?.(".community-split-pane") as HTMLElement | null;
    if (pane) pane.scrollTop = 0;
    this.postNotFound = false;
    // Paint from the feed's copy immediately — the list already holds the whole post and
    // its replies, so a click should swap the pane's contents with no loading state at all.
    // `getPost` still runs underneath: it resolves a reply to its parent thread and attaches
    // the live watchers, then overwrites these with the authoritative copy.
    const seeded = this._seedFromCache(value);
    if (!seeded) {
      this.post = undefined;
      this.replies = undefined;
      this._seededWithReplies = false;
    }
    this.getPost();
  }

  /**
   * Fill `post`/`replies` from the feed's in-memory maps, if this post is in them.
   *
   * `displayedPostsMap` is keyed by doc key and holds every parent AND reply currently
   * rendered; `activeParentThreadMap` holds each parent's replies. Both are already
   * populated for anything visible in the list, which is the only way to reach the pane.
   */
  private _seedFromCache(postId: string): boolean {
    const wanted = String(postId);
    // `displayedPostsMap` is keyed by Firestore doc `key`, while the list rows are addressed
    // by `id`. They coincide for most posts but not provably all, so fall back to a scan of
    // the parent list rather than assume — it is one pass over a single page of posts.
    const cached = this.communityService.displayedPostsMap?.[wanted] || (this.communityService.activeParentPosts || []).find((p: any) => String(p.id) === wanted || String(p.key) === wanted);
    // A reply has no thread of its own to show; let `getPost` resolve it to its parent.
    if (!cached || cached.parentID) return false;
    this.post = cached;
    const threadMap = this.communityService.activeParentThreadMap || {};
    const replies = threadMap[cached.key] || threadMap[cached.id];
    this.replies = replies || [];
    // Only a thread-map entry means the replies were actually loaded; a post that reached
    // `displayedPostsMap` some other way (search) can be here without them. `getPost` skips
    // its Firestore read only when this is set.
    // A list-layout feed skips loading replies (`_repliesDeferred`); its entry is a placeholder.
    this._seededWithReplies = !!replies && !cached._repliesDeferred;
    return true;
  }

  public postId: string;
  /** The current post and its full reply list came from the feed's memory — see `getPost`. */
  private _seededWithReplies = false;
  public parentPostId: string;
  public post: any;
  public replies: any[];
  public postNotFound: boolean;
  public postDetailRepliesSubscription: any;
  public postSubscription: any;

  constructor(
    userService: UserService,
    public activatedRoute: ActivatedRoute,
    public communityService: WebCommunityService,
    public helperService: HelperService,
    private _router: Router,
    public db: AngularFirestore,
    public fbService: FirebaseService,
    public betaService: BetaService,
    private _seo: SeoService,
    private _host: ElementRef
  ) {
    super(userService);
  }

  /**
   * The reader browses this community in list view, so this detail is the only place they see
   * the thread — show every reply rather than the last two. The collapse button stays.
   * Regular communities only; beta keeps its own layout.
   */
  public showAllReplies = false;

  /**
   * When you last opened this thread, read BEFORE this visit is recorded — `since` is `0` for
   * a first visit, else epoch millis; the whole thing is `null` while unknown. `<myp-comment>`
   * places "New replies" before the first reply newer than `since` and scrolls there, or
   * leaves you at the top on a first visit. See `WebCommunityService.getPostLastViewed`.
   *
   * Carries the thread id and is a NEW object per visit, on purpose: the comment's inputs are
   * applied in template order (post, replies, then this), so on a switch it briefly holds the
   * new thread with the previous thread's value — the id lets it ignore that, and the fresh
   * object re-arms the scroll even when two visits happen to carry the same `since`.
   */
  public newRepliesState: { threadId: string; since: number } | null = null;
  /** The thread whose visit is open, so leaving it (or switching posts) can close it out. */
  private _viewingPostId: string | null = null;

  /** Resolve the last visit, then record this one. Once per thread per visit. */
  private _openView(parentPostId: string) {
    if (!parentPostId || this._viewingPostId === parentPostId) return;
    this._viewingPostId = parentPostId;
    this.newRepliesState = null;
    if (!this.userService.user?.id) {
      this.newRepliesState = { threadId: parentPostId, since: 0 };
      return;
    }
    // Usually already in memory — the feed prefetches view records as its pages load — which
    // sets this in the same change-detection pass as the post itself, so the detail opens at
    // the right place with no top-then-jump. A post the feed never listed (link, search)
    // falls through to the read below.
    const known = this.communityService.peekPostLastViewed(parentPostId);
    if (known !== undefined) {
      this.newRepliesState = { threadId: parentPostId, since: known };
      this.communityService.markPostViewed(parentPostId);
      return;
    }
    this.communityService.getPostLastViewed(parentPostId).then((at) => {
      // Switched to another post while the read was in flight.
      if (this._viewingPostId !== parentPostId) return;
      this.newRepliesState = { threadId: parentPostId, since: at };
      this.communityService.markPostViewed(parentPostId);
    });
  }

  /**
   * Leaving a thread counts as having seen it to now, so replies that arrived WHILE you were
   * reading it aren't "new" next time.
   */
  private _closeView() {
    if (this._viewingPostId && this.newRepliesState !== null) this.communityService.markPostViewed(this._viewingPostId);
    this._viewingPostId = null;
    this.newRepliesState = null;
  }

  ngOnInit() {
    this.showAllReplies = !this.betaStringId && this.communityService.getCommunityViewMode(this.communityStringId) === "list";
    // `postIdInput` wins when the parent supplied one; the route is the fallback for the
    // standalone page.
    if (!this.postId) this.postId = this.activatedRoute.snapshot.params["id"];
    if (this.betaStringId) {
      this.betaService.gotBetas$
        .pipe(
          map((ready) => !!ready),
          skip(0),
          takeUntil(this.destroy$)
        )
        .subscribe((ready) => {
          if (ready) {
            const betaProgram = this.betaService.betaDetailByStringId[this.betaStringId];
            if (!betaProgram || !this.betaService.enrolledMap[betaProgram.string_id]) {
              this._router.navigate(["/beta"]);
            } else {
              if (this.postId) {
                this.getPost();
              }
            }
          }
        });
    } else {
      if (this.postId) {
        this.getPost();
      }
    }
  }

  /**
   * Drop the live Firestore watchers for the post currently on screen.
   *
   * Called when the pane switches posts and on destroy. `postSubscription` in particular
   * had no teardown at all before the pane existed — it only unsubscribed on the way to
   * re-subscribing, so navigating away left it running.
   */
  private _teardownWatchers() {
    if (this.postDetailRepliesSubscription) {
      this.postDetailRepliesSubscription.unsubscribe();
      this.postDetailRepliesSubscription = null;
    }
    if (this.postSubscription) {
      this.postSubscription.unsubscribe();
      this.postSubscription = null;
    }
  }

  ngOnDestroy() {
    this._teardownWatchers();
    this._closeView();
    super.ngOnDestroy();
  }

  /**
   * Bumped on every load. Each async step checks it still matches before touching the view,
   * so a slow read for a post you've already clicked away from can't paint over the one on
   * screen or attach its watchers to it — clicking A, B, A quickly used to flash B's thread
   * back in (and reset the scroll) when B's read landed last.
   */
  private _loadToken = 0;

  getPost() {
    const token = ++this._loadToken;
    const isBeta = this._router.url.includes("betapost");
    const collection = isBeta ? "beta_community_posts" : this.env.communityFirestoreCollection;

    // Already painted from the feed's memory (`_seedFromCache` only accepts parent posts):
    // nothing to read. Just attach the live watchers — both skip their first snapshot, so the
    // cached post and replies stay on screen untouched until something actually changes.
    // Re-reading here replaced every object and rebuilt the whole thread for no reason.
    if (this._seededWithReplies && this.post && String(this.post.id) === String(this.postId) && !this.post.parentID) {
      this.parentPostId = this.postId;
      this._applySeo();
      this._openView(this.parentPostId);
      this._attachWatchers(this.parentPostId, collection, token);
      return;
    }

    this.communityService
      .getPostByID(this.postId, undefined, undefined, collection)
      .then((result: any) => {
        if (token !== this._loadToken) return;
        this.parentPostId = this.postId;
        if (result[0] && result[0].parentID) {
          // A reply's id (a notification link) — show its whole thread.
          this.parentPostId = result[0].parentID;
          this.communityService
            .getPostByID(this.parentPostId)
            .then((result: any) => {
              if (token !== this._loadToken) return;
              this.post = result[0];
              this.replies = result.filter((item) => item.parentID);
              this._applySeo();
            })
            .catch((err) => {});
        } else {
          this.post = result[0];
          this.replies = result.filter((item) => item.parentID);
          this._applySeo();
        }
        // Beta threads too — the record is keyed by post id, which is unique across both collections.
        this._openView(this.parentPostId);
        this._attachWatchers(this.parentPostId, collection, token);
      })
      .catch((error) => {
        if (token !== this._loadToken) return;
        const collections = ["beta_community_posts", this.env.communityFirestoreCollection];
        const promises = collections.map((collection) => this.communityService.getPostByID(this.postId, true, false, collection, true).catch(() => null));

        Promise.all(promises).then((results: any[]) => {
          if (token !== this._loadToken) return;
          const post = results.find((result: any) => result !== null);
          if (post?.beta_string_id) {
            this._router.navigate(["/beta/" + post.beta_string_id + "/channels/all_posts"]);
          } else {
            this._router.navigate(["/community"]);
          }
        });
      });
  }

  /**
   * Live replies + live post for the thread on screen. Held rather than fire-and-forget: the
   * split pane swaps posts without remounting, and `takeUntil(destroy$)` alone only covers
   * unmount. Each emission re-checks the load token too, belt and braces for an emission
   * already in flight when `_teardownWatchers` ran.
   */
  private _attachWatchers(parentPostId: string, collection: string, token: number) {
    this._teardownWatchers();
    this.postDetailRepliesSubscription = this.communityService
      .watchPostReplies(parentPostId, collection)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (token !== this._loadToken) return;
        this.replies = result;
      });

    // also watch for updates on this post as it may be edited or change
    this.postSubscription = this.fbService
      .handleFirestoreObservable(() => {
        return this.db
          .collection(collection)
          .doc(parentPostId)
          .snapshotChanges()
          .pipe(
            skip(1),
            throttleTime(1000),
            map((result) => {
              return result;
            })
          );
      })
      .subscribe((result: any) => {
        if (token !== this._loadToken) return;
        // Deleting marks the document rather than removing it, so this watcher is the
        // only thing that hears about it. The feed's own watcher drops the row, which
        // left the detail still showing a post that is no longer anywhere in the list.
        const data = result?.payload?.exists === false ? null : result?.payload?.data?.();
        if (!data || data.deleted) {
          this.postDeleted();
          return;
        }
        this.updatePost();
      });
  }

  updatePost() {
    const token = this._loadToken;
    this.communityService
      .getPostByID(this.parentPostId, false, false, this.betaStringId ? "beta_community_posts" : this.env.communityFirestoreCollection)
      .then((result: any) => {
        if (token !== this._loadToken) return;
        this.post = result[0];
        this.replies = result.filter((item) => item.parentID);
        this._applySeo();
      })
      .catch((err) => {});
  }

  private _applySeo() {
    if (!this.post || this.betaStringId) return;
    // Only emit rich meta when community is meant to be publicly indexable.
    if (!(environment.features as any).communityPublicAccess) return;
    // The tab title and structured data are text — decode the entities the subject is stored with.
    const subject = htmlFragmentToText(this.post.subject);
    const message = this.post.message ? this.post.message.replace(/<[^>]*>/g, "") : "";
    const title = subject || (message ? message.substring(0, 80) : "Community Post") + " | MyFender Community";
    const description = (message || subject || "A community post on MyFender").substring(0, 200).trim();
    // Prefer the post's own `community_string_id` (source of truth) and fall back to the
    // route-bound input. Older posts pre-backfill may lack the field — those default to FSP.
    const community = this.post.community_string_id || this.communityStringId;
    const canonical =
      "https://my.fender.com/" +
      buildCommunityPostPath({
        postId: this.post.id || this.parentPostId,
        communityStringId: community,
      });
    this._seo.setPageMeta({
      title,
      description,
      canonical,
      ogImage: this.post.author?.photo,
      ogType: "article",
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "DiscussionForumPosting",
        headline: subject || description.substring(0, 80),
        articleBody: message,
        author: this.post.author?.name ? { "@type": "Person", name: this.post.author.name } : undefined,
        url: canonical,
      },
    });
  }

  /**
   * The post being read was deleted out from under us.
   *
   * Same exit as the member pressing back or close: the shell puts the feed back, where the
   * post no longer is, so nothing stale is left on screen. Standalone falls through to
   * `goBack()` — the only way out of a page whose subject is gone.
   */
  private postDeleted() {
    this._teardownWatchers();
    if (this.embedded || this.hosted) {
      this.closed.emit();
      return;
    }
    this.goBack();
  }

  /**
   * The back control. Hand it to the shell when there is one — see `hosted` — otherwise
   * navigate out of the standalone page.
   */
  back() {
    if (this.hosted) {
      this.closed.emit();
      return;
    }
    this.goBack();
  }

  goBack() {
    const cache = this.communityService.communityListBackCache;
    if (cache?.returnUrl) {
      // Restore cached data into the service before navigating.
      // The old channel component's ngOnDestroy calls unwatch() which clears the service data,
      // and the new channel component will read from the service directly.
      this.communityService.activeParentPosts = [...cache.activeParentPosts];
      this.communityService.activeParentThreadMap = {};
      for (const k in cache.activeParentThreadMap) {
        this.communityService.activeParentThreadMap[k] = [...(cache.activeParentThreadMap[k] || [])];
      }
      this.communityService.displayedPostsMap = {};
      this.communityService.activeParentPosts.forEach((p) => (this.communityService.displayedPostsMap[p.key] = p));
      for (const parentId in this.communityService.activeParentThreadMap) {
        this.communityService.activeParentThreadMap[parentId].forEach((r) => (this.communityService.displayedPostsMap[r.key] = r));
      }
      this.communityService.lastVisiblePost = cache.lastVisiblePost;
      this.communityService.activePostsLimitReached = cache.activePostsLimitReached;
      if (cache.betaTagCountMap) this.communityService.betaTagCountMap = cache.betaTagCountMap;
      if (cache.filteredBetaAuthorList) this.communityService.filteredBetaAuthorList = cache.filteredBetaAuthorList;
      this.communityService.restoringFromPostDetail = true;
      this._router.navigateByUrl(cache.returnUrl);
    } else if (this.activatedRoute.snapshot.queryParams["searchTerm"]) {
      this._router.navigate([this.betaStringId ? "/beta/" + this.betaStringId + "/channels/all_posts" : "/community"], {
        queryParams: { search: this.activatedRoute.snapshot.queryParams["searchTerm"] },
      });
    } else {
      this._router.navigate([this.betaStringId ? "/beta/" + this.betaStringId + "/channels/all_posts" : "/community"]);
    }
  }
}
